// src/pages/approval/ApprovalTypeEdit.jsx (경로는 기존 그대로)
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import * as approvalTypeApi from "../../api/approval/type";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import TableCell from "../../components/common/ui/TableCell";
import PageHeader from "../../components/common/ui/PageHeader";

export default function ApprovalTypeEdit() {
  const { id: projectId, typeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const editorRef = useRef(null);

  // ✅ mode: "add"는 신규 생성 모드
  const isNew = location.state?.mode === "add" || typeId === "new";

  const [form, setForm] = useState({
    id: "",
    name: "",
    code: "",
    title: "",
    content: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const editorKey = useMemo(() => `type-${form.id || "new"}`, [form.id]);

  // ✅ 수정 모드일 때만 서버에서 불러오기
  useEffect(() => {
    if (isNew || !projectId || !typeId) {
      // 새 유형 모드 → 기본값 세팅
      setForm({
        id: "",
        name: "",
        code: "",
        title: "",
        content: "",
      });
      return;
    }

    setLoading(true);
    approvalTypeApi
      .getTypeDetail(projectId, typeId)
      .then((data) => setForm(data))
      .catch(() => alert("유형을 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, [projectId, typeId, isNew]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ 저장
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      alert("양식명을 입력해주세요.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        content: editorRef.current?.getContent() || form.content,
      };

      await approvalTypeApi.saveType(projectId, payload);
      alert(isNew ? "새 유형이 추가되었습니다." : "저장되었습니다.");
      navigate(`/project/${projectId}/approval/type`);
    } catch (err) {
      alert("저장 실패");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* 헤더 */}
      <PageHeader showBack>
        {isNew ? "새 결재 문서 유형 등록" : "결재 문서 유형 편집"}
      </PageHeader>

      {loading && !isNew ? (
        <div className="text-center text-slate-500 py-8">
          불러오는 중...
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          {/* 카드 */}
          <div className="overflow-hidden">
            <table className="w-full text-sm">
              <tbody >
                {/* 양식명 */}
                <tr >
                  <TableCell
                    label="양식명"
                    editing
                    input={
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className="input-base"
                        placeholder="예: 품질, 설계, 일반"
                      />
                    }
                  />
                </tr>

                {/* 코드 */}
                <tr >
                  <TableCell
                    label="코드"
                    editing
                    input={
                      <input
                        name="code"
                        value={form.code}
                        onChange={handleChange}
                        disabled={!isNew} // ✅ 편집모드에서는 수정 불가
                        className={`input-base ${!isNew
                          ? "bg-slate-50 text-slate-500 cursor-not-allowed"
                          : ""
                          }`}
                        placeholder="코드(영어 대문자 2자)"
                        maxLength={2}
                      />
                    }
                  />
                </tr>

                {/* 제목 예시 */}
                <tr >
                  <TableCell
                    label="제목 예시"
                    editing
                    input={
                      <input
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        className="input-base"
                        placeholder="결재 문서 제목 예시"
                      />
                    }
                  />
                </tr>

                {/* 내용 템플릿 */}
                <tr className="divide-x align-top">
                  <TableCell
                    label="내용 템플릿"
                    editing
                    rich
                    input={
                      <TinyMCEeditor
                        key={editorKey}
                        editorRef={editorRef}
                        initialValue={form.content}
                      />
                    }
                  />
                </tr>
              </tbody>
            </table>
          </div>

          {/* 버튼 영역 */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-basic"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-create"
            >
              {saving ? "저장 중…" : isNew ? "등록" : "저장"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
